<template>
  <div>
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('accounting_transactions_filters') }}
          </h3>
        </b-col>
        <b-col cols="3">
          <b-form-input
            v-model="filter.keyword"
            :placeholder="$t('accounting_transactions_search')"
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.transaction_type"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="transactionTypeOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transactions_transaction_type')"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
            clearable
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.status"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transactions_status')"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
            clearable
          />
        </b-col>
        <b-col cols="3">
          <b-form-input
            v-model="filter.date_from"
            type="date"
            :placeholder="$t('accounting_accounts_from_date')"
          />
        </b-col>
        <b-col cols="3">
          <b-form-input
            v-model="filter.date_to"
            type="date"
            :placeholder="$t('accounting_accounts_to_date')"
          />
        </b-col>
      </b-row>
    </b-card>
    <b-card class="w-100 d-flex rounded-lg">
      <b-row>
        <b-col
          md="12"
          class="pb-2 w-100 d-flex justify-content-between"
        >
          <b-dropdown
            v-if="hasPermission('create account transactions')"
            variant="primary"
            right
            class="ml-auto"
          >
            <template #button-content>
              {{ $t('accounting_transactions_add_transaction') }}
            </template>
            <b-dropdown-item @click="gotoCreateTransaction('cash-in')">
              {{ $t('accounting_transactions_create_cash_in') }}
            </b-dropdown-item>
            <b-dropdown-item @click="gotoCreateTransaction('cash-out')">
              {{ $t('accounting_transactions_create_cash_out') }}
            </b-dropdown-item>
            <b-dropdown-item @click="gotoCreateTransaction('transfer')">
              {{ $t('accounting_transactions_create_transfer') || 'Transfer' }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
      </b-row>

      <div
        v-if="load"
        class="text-center"
        style="width: 100%;"
      >
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </div>

      <b-table
        v-else
        striped
        :items="safeItems.data || safeItems"
        :fields="fields"
        responsive
      >
        <template #cell(transaction_type)="data">
          <b-badge :variant="getTransactionTypeVariant((data.item.transaction_type && data.item.transaction_type.value) || '')">
            {{ $i18n.locale === 'ar' ? (data.item.transaction_type && data.item.transaction_type.label_ar) || '' : (data.item.transaction_type && data.item.transaction_type.label) || '' }}
          </b-badge>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant((data.item.status && data.item.status.value) || '')">
            {{ $i18n.locale === 'ar' ? (data.item.status && data.item.status.label_ar) || '' : (data.item.status && data.item.status.label) || '' }}
          </b-badge>
        </template>
        <template #cell(amount)="data">
          {{ formatCurrency(data.item.amount, data.item.currency) }}
        </template>
        <template #cell(fee)="data">
          {{ formatCurrency(data.item.fee || 0, data.item.currency) }}
        </template>
        <template #cell(invoice)="data">
          <b-link
            v-if="invoiceOf(data.item)"
            :href="invoiceOf(data.item).url || invoiceOf(data.item).download_url"
            target="_blank"
          >
            {{ invoiceOf(data.item).original_name || invoiceOf(data.item).file_name || $t('accounting_transactions_invoice') }}
          </b-link>
          <span v-else>—</span>
        </template>
        <template #cell(transaction_date)="data">
          {{ formatDate(data.item.transaction_date) }}
        </template>
        <template #cell(drawer)="data">
          {{ voucherSourceText(data.item) }}
        </template>
        <template #cell(paid_from)="data">
          {{ paidFromText(data.item) }}
        </template>
        <template #cell(received_by)="data">
          {{ receivedByText(data.item) }}
        </template>
        <template #cell(actions)="data">
          <div
            v-if="data.item"
            class="d-flex align-items-center"
          >
            <b-link
              v-b-tooltip.hover.top="$t('accounting_transactions_view_transaction')"
              class="ml-1"
              :to="{ name: 'accounting-transactions-view', params: { id: data.item.id} }"
            >
              <feather-icon
                icon="EyeIcon"
                size="18"
              />
            </b-link>
            <b-link
              v-if="(data.item.status && data.item.status.value === 'pending') && hasPermission('approve account transactions')"
              v-b-tooltip.hover.top="$t('accounting_transactions_approve')"
              class="ml-1 text-success"
              @click="approveTransaction(data.item.id)"
            >
              <feather-icon
                icon="CheckIcon"
                size="18"
              />
            </b-link>
            <b-link
              v-if="(data.item.status && data.item.status.value === 'pending') && hasPermission('cancel account transactions')"
              v-b-tooltip.hover.top="$t('accounting_transactions_cancel')"
              class="ml-1 text-danger"
              @click="cancelTransaction(data.item.id)"
            >
              <feather-icon
                icon="XIcon"
                size="18"
              />
            </b-link>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="safeItems.meta"
        v-model="currentPage"
        align="center"
        :total-rows="safeItems.meta.total"
        :per-page="safeItems.meta.per_page"
        @change="onPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BDropdown,
  BDropdownItem,
  BCard,
  BRow,
  BCol,
  BBadge,
  BLink,
  VBTooltip,
  BSpinner,
  BPagination,
  BFormInput,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { invoiceMedia } from '@/utils/transactionMoney'
import {
  paidFromText, receivedByText, voucherErrorMessage, voucherSourceText,
} from '@/utils/voucherSource'

export default {
  components: {
    BTable,
    BButton,
    BDropdown,
    BDropdownItem,
    BCard,
    BRow,
    BCol,
    BBadge,
    BLink,
    BSpinner,
    BPagination,
    BFormInput,
    vSelect,
    FeatherIcon,
    ToastificationContent,
  },
  computed: {
    ...mapGetters({
      items: 'accounting/transactions/items',
      load: 'accounting/transactions/load',
    }),
    safeItems() {
      return this.items || { data: [], meta: null }
    },
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  watch: {
    'filter.keyword': function () {
      this.debounceSearch()
    },
    'filter.transaction_type': function () {
      this.loadTransactions()
    },
    'filter.status': function () {
      this.loadTransactions()
    },
    'filter.date_from': function () {
      this.loadTransactions()
    },
    'filter.date_to': function () {
      this.loadTransactions()
    },
  },
  data() {
    return {
      currentPage: 1,
      filter: {
        keyword: null,
        transaction_type: null,
        status: null,
        date_from: null,
        date_to: null,
        per_page: 15,
        page: 1,
      },
      transactionTypeOptions: [
        { value: 'cash_in', label: 'Cash In', label_ar: 'إيداع' },
        { value: 'cash_out', label: 'Cash Out', label_ar: 'سحب' },
        { value: 'transfer', label: 'Transfer', label_ar: 'تحويل' },
      ],
      statusOptions: [
        { value: 'pending', label: 'Pending', label_ar: 'قيد الانتظار' },
        { value: 'approved', label: 'Approved', label_ar: 'معتمد' },
        { value: 'cancelled', label: 'Cancelled', label_ar: 'ملغي' },
      ],
      fields: [],
      searchTimeout: null,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    gotoCreateTransaction(type = 'cash-in') {
      const routeMap = {
        'cash-in': 'accounting-transactions-create-cash-in',
        'cash-out': 'accounting-transactions-create-cash-out',
        transfer: 'accounting-transactions-create-transfer',
      }
      this.$router.push({ name: routeMap[type] || routeMap['cash-in'] })
    },
    onPageChange(page) {
      this.currentPage = page
      this.filter.page = page
      this.loadTransactions()
    },
    loadTransactions() {
      const params = { ...this.filter }
      if (!params.keyword) delete params.keyword
      if (!params.transaction_type) delete params.transaction_type
      if (!params.status) delete params.status
      if (!params.date_from) delete params.date_from
      if (!params.date_to) delete params.date_to
      this.$store.dispatch('accounting/transactions/list', params)
    },
    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.filter.page = 1
        this.currentPage = 1
        this.loadTransactions()
      }, 500)
    },
    invoiceOf(item) {
      return invoiceMedia(item)
    },
    voucherSourceText,
    paidFromText,
    receivedByText,
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US')
    },
    getTransactionTypeVariant(type) {
      const variants = {
        cash_in: 'success',
        cash_out: 'danger',
        transfer: 'info',
      }
      return variants[type] || 'secondary'
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        approved: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    async approveTransaction(id) {
      try {
        await this.$store.dispatch('accounting/transactions/approve', id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.approved_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransactions()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: voucherErrorMessage(error, this.$t('accounting_transactions_messages.error')),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async cancelTransaction(id) {
      try {
        await this.$store.dispatch('accounting/transactions/cancel', { id })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.cancelled_success'),
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransactions()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transactions_messages.error'),
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    init() {
      this.fields = [
        { key: 'number', label: this.$t('accounting_transactions_number'), sortable: true },
        { key: 'transaction_type', label: this.$t('accounting_transactions_type'), sortable: true },
        { key: 'amount', label: this.$t('accounting_transactions_amount'), sortable: true },
        { key: 'fee', label: this.$t('accounting_transactions_fee'), sortable: true },
        { key: 'invoice', label: this.$t('accounting_transactions_invoice') },
        { key: 'currency', label: this.$t('accounting_accounts_currency') },
        { key: 'drawer', label: this.$t('accounting_drawer') },
        { key: 'paid_from', label: this.$t('accounting_paid_from') },
        { key: 'received_by', label: this.$t('accounting_received_by') },
        { key: 'transaction_date', label: this.$t('accounting_transactions_date'), sortable: true },
        { key: 'status', label: this.$t('accounting_transactions_status') },
        { key: 'actions', label: this.$t('accounting_accounts_actions') },
      ]
      this.loadTransactions()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
